import { useMemo, useState } from 'react'
import * as AreaHttp from '../../../../services/api/https/picking/picking-area.http'
import { errorHandler } from '../../../../services/util/error.service'
import { openReportInNewTab } from '../../../../services/util/report.service'
import { urlGetTagAreas } from '../../../../services/api/routes/picking.routes'

export const normalizeText = (text) => {
  if (typeof text !== 'string') return ''
  return text
    .replace(/\u00A0/g, ' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .toUpperCase()
}

export const normalizeTextForComparison = (text) => {
  return normalizeText(text).trim()
}

export const isValidAreaName = (name) => {
  const regex = /^[A-Z0-9\s]+$/
  return regex.test(name.trim())
}

export const isDuplicateAreaName = (name, areas, currentAreaId = null) => {
  const normalizedName = normalizeTextForComparison(name)
  return areas.some(area => {
    if (currentAreaId && area.id === currentAreaId) {
      return false
    }
    return normalizeTextForComparison(area.name) === normalizedName
  })
}

const getNewArea = selectedTp => {
  return {
    id: null,
    name: '',
    actived: true,
    tp_id: selectedTp,
    sequence: '',
  }
}

const INITIAL_STATE = {
  isLoading: false,
  areas: [],
  selectedArea: null,
  showDialog: 'NONE',
  errorMessage: null,
  selectedTp: null,
  selecionarTodos: false,
  qte: 0,
  name: 'AREA',
  comecaEm: 1,
}

export default function useAreaSeparacao(selectedTp) {
  const [state, setState] = useState({ ...INITIAL_STATE, selectedArea: getNewArea(selectedTp) })

  const localDispatch = useMemo(() => {
    return {
      showDialog(menuItem, area) {
        if (menuItem === 'Editar' && area) {
          setState(prev => ({ ...prev, showDialog: 'EDITAR', selectedArea: area }))
        } else if (menuItem === 'Editar' && !area) {
          setState(prev => ({
            ...prev,
            showDialog: 'EDITAR',
            selectedArea: getNewArea(prev.selectedTp),
          }))
        } else if (menuItem === 'Assoc. Cliente' && area) {
          setState(prev => ({ ...prev, showDialog: 'ASSOC CLIENTE', selectedArea: area }))
        }
      },

      hideDialog() {
        setState(prev => ({
          ...prev,
          showDialog: 'NONE',
          selectedArea: getNewArea(prev.selectedTp),
        }))
      },

      resetError() {
        setState(prev => ({ ...prev, errorMessage: null }))
      },

      setSelectedTp(selectedTp, cb) {
        if (!selectedTp) setState(prevState => ({ ...prevState, errorMessage: 'Selecione um TP' }))
        else {
          setState(prev => ({ ...prev, isLoading: true, selectedTp }))
          AreaHttp.getAreas(selectedTp)
            .then(cb)
            .catch(e =>
              setState(prev => ({ ...prev, isLoading: false, errorMessage: errorHandler(e) })),
            )
        }
      },

      atualizarAreas(areas) {
        setState(prev => ({ ...prev, isLoading: false, areas }))
      },

      onEditarDialogResponse(type, payload, areas) {
        if (type === 'success') {
          const { id } = payload
          const index = areas.findIndex(a => a.id === id)
          if (index === -1) {
            setState(prev => {
              prev.areas.push(payload)
              prev.showDialog = false
              return { ...prev }
            })
          } else {
            setState(prev => {
              prev.areas[index] = payload
              prev.showDialog = false
              return { ...prev }
            })
          }
        } else {
          setState(prev => ({ ...prev, errorMessage: payload }))
        }
      },

      onAssocClienteDialogResponse(type, payload) {
        if (type === 'success') {
          const selectedArea = payload
          setState(prev => {
            const index = prev.areas.findIndex(a => a.id === selectedArea.id)
            if (index !== -1) {
              prev.areas[index] = selectedArea
            }
            return { ...prev, showDialog: 'NONE' }
          })
        } else {
          setState(prev => ({ ...prev, errorMessage: payload }))
        }
      },

      onSelecionarTodosChange() {
        setState(prev => {
          prev.selecionarTodos = !prev.selecionarTodos
          prev.areas.forEach(a => (a.selected = prev.selecionarTodos))
          return { ...prev }
        })
      },

      onItemCheckboxClick(item) {
        const { id } = item
        setState(prev => {
          const index = prev.areas.findIndex(a => a.id === id)
          if (index !== -1) {
            prev.areas[index].selected = prev.areas[index].selected !== true
          }
          return { ...prev }
        })
      },

      onBtnGerarTagAreaClick(areas) {
        const areasSelecionadas = areas.filter(a => a.selected)
        if (areasSelecionadas.length === 0) {
          setState(prev => ({ ...prev, errorMessage: 'Não existe área selecionada' }))
        } else {
          const param = areasSelecionadas.map(a => a.id).join(',')
          const url = urlGetTagAreas(param)
          openReportInNewTab(url)
        }
      },

      onInputChange(name, value) {
        setState(prevState => {
          if (name === 'name' && typeof value === 'string') {
            value = normalizeText(value)
          }
          prevState[name] = value
          return { ...prevState }
        })
      },

      async onGerarAreasClick(qte, name, comecaEm, selectedTp, areas) {
        if (!selectedTp) {
          setState(prevState => ({ ...prevState, errorMessage: 'Selecione um Operador Logístico' }))
          return
        }

        const qteNum = Number(qte)
        const startNum = Number(comecaEm)

        if (qteNum < 1 || Number.isNaN(qteNum)) {
          setState(prevState => ({ ...prevState, errorMessage: 'Insira uma quantidade maior que 0.' }))
          return
        }

        if (name.trim() === '') {
          setState(prevState => ({ ...prevState, errorMessage: 'Insira um nome válido' }))
          return
        }

        if (!isValidAreaName(name)) {
          setState(prevState => ({
            ...prevState,
            errorMessage: 'O nome da área deve conter apenas letras maiúsculas e números, sem acentuação'
          }))
          return
        }

        if (startNum < 1 || Number.isNaN(startNum)) {
          setState(prevState => ({
            ...prevState,
            errorMessage: 'O contador precisa iniciar em um número maior ou igual a 1',
          }))
          return
        }

        const duplicates = []
        for (let i = 0; i < qteNum; i++) {
          const areaName = `${name} ${startNum + i}`
          if (isDuplicateAreaName(areaName, areas)) {
            duplicates.push(areaName)
          }
        }

        if (duplicates.length > 0) {
          setState(prevState => ({
            ...prevState,
            errorMessage: `As seguintes áreas já existem: ${duplicates.join(', ')}`,
          }))
          return
        }

        try {
          setState(prevState => ({ ...prevState, isLoading: true }))
          await AreaHttp.saveMultiplesArea(qteNum, name, startNum, selectedTp)
          localDispatch.setSelectedTp(selectedTp, areas => localDispatch.atualizarAreas(areas))
        } catch (err) {
          setState(prevState => ({
            ...prevState,
            errorMessage: errorHandler(err),
            isLoading: false
          }))
        } finally {
          setState(prevState => ({ ...prevState, isLoading: false }))
        }
      },
    }
  }, [])

  return { state, localDispatch }
}